// Social Media Analytics Service
import { supabase } from '@/config/supabase';
import { socialMediaAPI } from './platformAPIs';

interface PostAnalytics {
  post_id: string;
  platform: string;
  impressions: number;
  reach: number;
  engagement: number;
  clicks: number;
  likes: number;
  comments: number;
  shares: number;
  saves?: number;
  video_views?: number;
  engagement_rate: number;
  collected_at: string;
  collection_label: string; // '1h', '24h', '7d'
}

interface CampaignMetrics {
  campaign_id: string;
  campaign_name: string;
  total_impressions: number;
  total_reach: number;
  total_engagement: number;
  total_clicks: number;
  total_spend?: number;
  roi?: number;
  conversion_rate?: number;
  cost_per_engagement?: number;
  start_date: string;
  end_date: string;
  platforms: string[];
  posts_count: number;
}

interface AudienceInsights {
  demographics: {
    age_groups: Record<string, number>;
    gender: Record<string, number>;
    locations: Record<string, number>;
    languages: Record<string, number>;
  };
  peak_times: {
    hour: number;
    engagement: number;
  }[];
  top_content_types: {
    type: string;
    engagement_rate: number;
  }[];
  growth_metrics: {
    followers_count: number;
    followers_growth: number;
    avg_engagement_rate: number;
  };
}

export class SocialMediaAnalytics {
  
  // Collect analytics for a published post
  async collectPostAnalytics(
    postId: string,
    publishedIds: Record<string, string>,
    collectionLabel: string = 'manual'
  ): Promise<PostAnalytics[]> {
    const analytics: PostAnalytics[] = [];

    try {
      // Get metrics from each platform
      const metrics = await socialMediaAPI.getAggregatedMetrics(publishedIds);

      // Calculate engagement rate
      const engagementRate = metrics.impressions > 0 
        ? (metrics.engagement / metrics.impressions) * 100 
        : 0;

      // Save analytics for each platform
      for (const [platform, platformPostId] of Object.entries(publishedIds)) {
        const platformAnalytics: PostAnalytics = {
          post_id: postId,
          platform,
          impressions: metrics.impressions,
          reach: metrics.reach,
          engagement: metrics.engagement,
          clicks: metrics.clicks,
          likes: metrics.likes,
          comments: metrics.comments,
          shares: metrics.shares,
          engagement_rate: engagementRate,
          collected_at: new Date().toISOString(),
          collection_label: collectionLabel
        };

        analytics.push(platformAnalytics);

        // Save to database
        await supabase
          .from('post_analytics')
          .insert(platformAnalytics);
      }

      // Update analytics collection queue
      await supabase
        .from('analytics_collection_queue')
        .update({ status: 'completed' })
        .eq('post_id', postId)
        .eq('collection_label', collectionLabel);

    } catch (error) {
      console.error('Error collecting post analytics:', error);
      
      // Mark as failed in queue
      await supabase
        .from('analytics_collection_queue')
        .update({ 
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error'
        })
        .eq('post_id', postId)
        .eq('collection_label', collectionLabel);
    }

    return analytics;
  }

  // Get post performance over time
  async getPostPerformance(postId: string): Promise<{
    timeline: PostAnalytics[];
    summary: {
      total_impressions: number;
      total_reach: number;
      total_engagement: number;
      avg_engagement_rate: number;
      best_platform: string;
    };
  }> {
    const { data, error } = await supabase
      .from('post_analytics')
      .select('*')
      .eq('post_id', postId)
      .order('collected_at', { ascending: true });

    if (error || !data) {
      return {
        timeline: [],
        summary: {
          total_impressions: 0,
          total_reach: 0,
          total_engagement: 0,
          avg_engagement_rate: 0,
          best_platform: 'unknown'
        }
      };
    }

    // Calculate summary
    const summary = data.reduce((acc, curr) => {
      acc.total_impressions += curr.impressions;
      acc.total_reach = Math.max(acc.total_reach, curr.reach); // Use max for reach
      acc.total_engagement += curr.engagement;
      
      // Track best platform
      if (!acc.platforms[curr.platform]) {
        acc.platforms[curr.platform] = 0;
      }
      acc.platforms[curr.platform] += curr.engagement;
      
      return acc;
    }, {
      total_impressions: 0,
      total_reach: 0,
      total_engagement: 0,
      platforms: {} as Record<string, number>
    });

    // Find best platform
    const bestPlatform = Object.entries(summary.platforms)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'unknown';

    // Calculate average engagement rate
    const avgEngagementRate = summary.total_impressions > 0
      ? (summary.total_engagement / summary.total_impressions) * 100
      : 0;

    return {
      timeline: data,
      summary: {
        total_impressions: summary.total_impressions,
        total_reach: summary.total_reach,
        total_engagement: summary.total_engagement,
        avg_engagement_rate: avgEngagementRate,
        best_platform: bestPlatform
      }
    };
  }

  // Get campaign metrics
  async getCampaignMetrics(
    campaignId: string,
    startDate: Date,
    endDate: Date
  ): Promise<CampaignMetrics> {
    // Get all posts in the campaign
    const { data: posts } = await supabase
      .from('scheduled_posts')
      .select('id, published_ids')
      .eq('campaign_id', campaignId)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());

    if (!posts || posts.length === 0) {
      return {
        campaign_id: campaignId,
        campaign_name: '',
        total_impressions: 0,
        total_reach: 0,
        total_engagement: 0,
        total_clicks: 0,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        platforms: [],
        posts_count: 0
      };
    }

    // Get analytics for all posts
    const postIds = posts.map(p => p.id);
    const { data: analytics } = await supabase
      .from('post_analytics')
      .select('*')
      .in('post_id', postIds);

    if (!analytics) {
      return {
        campaign_id: campaignId,
        campaign_name: '',
        total_impressions: 0,
        total_reach: 0,
        total_engagement: 0,
        total_clicks: 0,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        platforms: [],
        posts_count: posts.length
      };
    }

    // Aggregate metrics
    const metrics = analytics.reduce((acc, curr) => {
      acc.total_impressions += curr.impressions;
      acc.total_reach = Math.max(acc.total_reach, curr.reach);
      acc.total_engagement += curr.engagement;
      acc.total_clicks += curr.clicks;
      
      if (!acc.platforms.includes(curr.platform)) {
        acc.platforms.push(curr.platform);
      }
      
      return acc;
    }, {
      total_impressions: 0,
      total_reach: 0,
      total_engagement: 0,
      total_clicks: 0,
      platforms: [] as string[]
    });

    return {
      campaign_id: campaignId,
      campaign_name: `Campaign ${campaignId}`,
      ...metrics,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      posts_count: posts.length
    };
  }

  // Get platform comparison
  async getPlatformComparison(
    dateRange: { start: Date; end: Date }
  ): Promise<{
    platform: string;
    posts: number;
    impressions: number;
    engagement: number;
    engagement_rate: number;
  }[]> {
    const { data } = await supabase
      .from('post_analytics')
      .select('*')
      .gte('collected_at', dateRange.start.toISOString())
      .lte('collected_at', dateRange.end.toISOString());

    if (!data) return [];

    // Group by platform
    const platformData = data.reduce((acc, curr) => {
      if (!acc[curr.platform]) {
        acc[curr.platform] = {
          platform: curr.platform,
          posts: 0,
          impressions: 0,
          engagement: 0
        };
      }
      
      acc[curr.platform].posts++;
      acc[curr.platform].impressions += curr.impressions;
      acc[curr.platform].engagement += curr.engagement;
      
      return acc;
    }, {} as Record<string, any>);

    // Calculate engagement rates
    return Object.values(platformData).map((p: any) => ({
      ...p,
      engagement_rate: p.impressions > 0 ? (p.engagement / p.impressions) * 100 : 0
    }));
  }

  // Get best performing content
  async getTopContent(
    limit: number = 10,
    metric: 'impressions' | 'engagement' | 'clicks' = 'engagement'
  ): Promise<{
    post_id: string;
    content: string;
    platforms: string[];
    metric_value: number;
    published_at: string;
  }[]> {
    // Get top posts by metric
    const { data: analytics } = await supabase
      .from('post_analytics')
      .select('post_id, impressions, engagement, clicks')
      .order(metric, { ascending: false })
      .limit(limit);

    if (!analytics) return [];

    // Get post details
    const postIds = [...new Set(analytics.map(a => a.post_id))];
    const { data: posts } = await supabase
      .from('scheduled_posts')
      .select('id, content, platforms, created_at')
      .in('id', postIds);

    if (!posts) return [];

    // Combine data
    return posts.map(post => {
      const postAnalytics = analytics.filter(a => a.post_id === post.id);
      const totalMetric = postAnalytics.reduce((sum, a) => sum + a[metric], 0);
      
      return {
        post_id: post.id,
        content: post.content.substring(0, 100) + '...',
        platforms: post.platforms,
        metric_value: totalMetric,
        published_at: post.created_at
      };
    }).sort((a, b) => b.metric_value - a.metric_value);
  }

  // Get engagement trends
  async getEngagementTrends(
    period: 'day' | 'week' | 'month' = 'week',
    duration: number = 4
  ): Promise<{
    date: string;
    impressions: number;
    engagement: number;
    engagement_rate: number;
  }[]> {
    const endDate = new Date();
    const startDate = new Date();
    
    switch (period) {
      case 'day':
        startDate.setDate(startDate.getDate() - duration);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - (duration * 7));
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - duration);
        break;
    }

    const { data } = await supabase
      .from('post_analytics')
      .select('*')
      .gte('collected_at', startDate.toISOString())
      .lte('collected_at', endDate.toISOString())
      .order('collected_at', { ascending: true });

    if (!data) return [];

    // Group by period
    const grouped = data.reduce((acc, curr) => {
      const date = new Date(curr.collected_at);
      let key: string;
      
      switch (period) {
        case 'day':
          key = date.toISOString().split('T')[0];
          break;
        case 'week':
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          key = weekStart.toISOString().split('T')[0];
          break;
        case 'month':
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          break;
        default:
          key = date.toISOString().split('T')[0];
      }
      
      if (!acc[key]) {
        acc[key] = {
          date: key,
          impressions: 0,
          engagement: 0
        };
      }
      
      acc[key].impressions += curr.impressions;
      acc[key].engagement += curr.engagement;
      
      return acc;
    }, {} as Record<string, any>);

    // Calculate engagement rates
    return Object.values(grouped).map((g: any) => ({
      ...g,
      engagement_rate: g.impressions > 0 ? (g.engagement / g.impressions) * 100 : 0
    }));
  }

  // Get hashtag performance
  async getHashtagPerformance(): Promise<{
    hashtag: string;
    usage_count: number;
    avg_engagement: number;
    total_impressions: number;
  }[]> {
    const { data: posts } = await supabase
      .from('scheduled_posts')
      .select('id, hashtags, published_ids')
      .not('hashtags', 'is', null);

    if (!posts) return [];

    // Get analytics for posts with hashtags
    const postIds = posts.map(p => p.id);
    const { data: analytics } = await supabase
      .from('post_analytics')
      .select('post_id, impressions, engagement')
      .in('post_id', postIds);

    if (!analytics) return [];

    // Analyze hashtag performance
    const hashtagData: Record<string, {
      usage_count: number;
      total_engagement: number;
      total_impressions: number;
    }> = {};

    posts.forEach(post => {
      const postAnalytics = analytics.filter(a => a.post_id === post.id);
      const totalEngagement = postAnalytics.reduce((sum, a) => sum + a.engagement, 0);
      const totalImpressions = postAnalytics.reduce((sum, a) => sum + a.impressions, 0);
      
      post.hashtags?.forEach((hashtag: string) => {
        if (!hashtagData[hashtag]) {
          hashtagData[hashtag] = {
            usage_count: 0,
            total_engagement: 0,
            total_impressions: 0
          };
        }
        
        hashtagData[hashtag].usage_count++;
        hashtagData[hashtag].total_engagement += totalEngagement;
        hashtagData[hashtag].total_impressions += totalImpressions;
      });
    });

    // Calculate averages
    return Object.entries(hashtagData).map(([hashtag, data]) => ({
      hashtag,
      usage_count: data.usage_count,
      avg_engagement: data.total_engagement / data.usage_count,
      total_impressions: data.total_impressions
    })).sort((a, b) => b.avg_engagement - a.avg_engagement);
  }

  // Calculate ROI
  async calculateROI(
    campaignId: string,
    adSpend: number,
    conversionValue: number
  ): Promise<{
    roi: number;
    roi_percentage: number;
    cost_per_engagement: number;
    cost_per_click: number;
    revenue_generated: number;
  }> {
    const { data: analytics } = await supabase
      .from('post_analytics')
      .select('engagement, clicks')
      .eq('campaign_id', campaignId);

    if (!analytics) {
      return {
        roi: 0,
        roi_percentage: 0,
        cost_per_engagement: 0,
        cost_per_click: 0,
        revenue_generated: 0
      };
    }

    const totalEngagement = analytics.reduce((sum, a) => sum + a.engagement, 0);
    const totalClicks = analytics.reduce((sum, a) => sum + a.clicks, 0);
    
    const revenue = totalClicks * conversionValue;
    const roi = revenue - adSpend;
    const roiPercentage = adSpend > 0 ? (roi / adSpend) * 100 : 0;
    
    return {
      roi,
      roi_percentage: roiPercentage,
      cost_per_engagement: totalEngagement > 0 ? adSpend / totalEngagement : 0,
      cost_per_click: totalClicks > 0 ? adSpend / totalClicks : 0,
      revenue_generated: revenue
    };
  }

  // Get audience insights (mock data for now, would need platform APIs)
  async getAudienceInsights(): Promise<AudienceInsights> {
    // This would typically come from platform APIs
    // For now, returning mock data structure
    return {
      demographics: {
        age_groups: {
          '18-24': 15,
          '25-34': 35,
          '35-44': 25,
          '45-54': 15,
          '55+': 10
        },
        gender: {
          'male': 45,
          'female': 52,
          'other': 3
        },
        locations: {
          'Warszawa': 25,
          'Kraków': 15,
          'Gdańsk': 20,
          'Wrocław': 10,
          'Poznań': 8,
          'Inne': 22
        },
        languages: {
          'Polski': 85,
          'Angielski': 10,
          'Inne': 5
        }
      },
      peak_times: [
        { hour: 8, engagement: 120 },
        { hour: 12, engagement: 180 },
        { hour: 15, engagement: 150 },
        { hour: 18, engagement: 200 },
        { hour: 20, engagement: 160 }
      ],
      top_content_types: [
        { type: 'Realizacje', engagement_rate: 4.5 },
        { type: 'Porady', engagement_rate: 3.8 },
        { type: 'Nowości', engagement_rate: 3.2 },
        { type: 'Behind the scenes', engagement_rate: 2.9 }
      ],
      growth_metrics: {
        followers_count: 5420,
        followers_growth: 12.5, // percentage
        avg_engagement_rate: 3.7
      }
    };
  }

  // Generate analytics report
  async generateReport(
    startDate: Date,
    endDate: Date,
    platforms?: string[]
  ): Promise<{
    summary: {
      total_posts: number;
      total_impressions: number;
      total_engagement: number;
      avg_engagement_rate: number;
      best_post_id: string;
      worst_post_id: string;
    };
    platform_breakdown: any[];
    daily_metrics: any[];
    top_content: any[];
    hashtag_performance: any[];
  }> {
    // Get all analytics in date range
    let query = supabase
      .from('post_analytics')
      .select('*')
      .gte('collected_at', startDate.toISOString())
      .lte('collected_at', endDate.toISOString());

    if (platforms && platforms.length > 0) {
      query = query.in('platform', platforms);
    }

    const { data: analytics } = await query;

    if (!analytics || analytics.length === 0) {
      return {
        summary: {
          total_posts: 0,
          total_impressions: 0,
          total_engagement: 0,
          avg_engagement_rate: 0,
          best_post_id: '',
          worst_post_id: ''
        },
        platform_breakdown: [],
        daily_metrics: [],
        top_content: [],
        hashtag_performance: []
      };
    }

    // Calculate summary
    const postPerformance = analytics.reduce((acc, curr) => {
      if (!acc[curr.post_id]) {
        acc[curr.post_id] = {
          impressions: 0,
          engagement: 0
        };
      }
      acc[curr.post_id].impressions += curr.impressions;
      acc[curr.post_id].engagement += curr.engagement;
      return acc;
    }, {} as Record<string, any>);

    const sortedPosts = Object.entries(postPerformance)
      .sort(([, a], [, b]) => b.engagement - a.engagement);

    const totalImpressions = analytics.reduce((sum, a) => sum + a.impressions, 0);
    const totalEngagement = analytics.reduce((sum, a) => sum + a.engagement, 0);

    return {
      summary: {
        total_posts: Object.keys(postPerformance).length,
        total_impressions: totalImpressions,
        total_engagement: totalEngagement,
        avg_engagement_rate: totalImpressions > 0 ? (totalEngagement / totalImpressions) * 100 : 0,
        best_post_id: sortedPosts[0]?.[0] || '',
        worst_post_id: sortedPosts[sortedPosts.length - 1]?.[0] || ''
      },
      platform_breakdown: await this.getPlatformComparison({ start: startDate, end: endDate }),
      daily_metrics: await this.getEngagementTrends('day', 30),
      top_content: await this.getTopContent(5),
      hashtag_performance: await this.getHashtagPerformance()
    };
  }
}

// Export singleton instance
export const socialMediaAnalytics = new SocialMediaAnalytics();
